// src/pages/superadmin/NearbyAirportsManager.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Plus, Search, RefreshCw, Edit2, Trash2, X, Upload,
  MapPin, Phone, Mail, Fuel, FileText, ChevronLeft,
  ChevronRight, CheckCircle, AlertCircle, ExternalLink,
  Plane, Globe, Radio, Wind
} from 'lucide-react';
import airportsData from '../../data/airports.json';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ⚠️ CORRECTION : La structure du JSON est { airports: { ... } }
const AIRPORTS_MAP = airportsData.airports || {};

// ─── Helpers ────────────────────────────────────────────────────────────────

const lookupIcao = (icao) => {
  if (!icao || !AIRPORTS_MAP) return null;
  return AIRPORTS_MAP[icao.toUpperCase()] || null;
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

/**
 * Fusionne les données de l'API (prix fuel) avec les données statiques du JSON
 * Les données API ont PRIORITÉ pour les champs qu'elles contiennent
 */
const mergeWithJsonData = (apiAirport) => {
  if (!apiAirport || !apiAirport.airport_code) return apiAirport;
  
  const jsonData = lookupIcao(apiAirport.airport_code);
  if (!jsonData) return apiAirport;
  
  // Fusion : les données API écrasent les données JSON
  return {
    ...jsonData,      // Données statiques du JSON
    ...apiAirport,    // Données API (prix fuel, last_updated, etc.)
    
    // S'assurer que les champs spécifiques à l'API sont préservés
    fuel_prices: apiAirport.fuel_prices,
    price_per_gallon: apiAirport.price_per_gallon,
    fuel_type: apiAirport.fuel_type,
    last_updated: apiAirport.last_updated,
    last_verified_at: apiAirport.last_verified_at,
    approach_charts: apiAirport.approach_charts,
    id: apiAirport.id,
  };
};

// ─── Badge email ─────────────────────────────────────────────────────────────

const EmailBadge = ({ email }) => email
  ? <span className="flex items-center gap-1 text-xs text-green-400"><CheckCircle className="w-3 h-3" />{email}</span>
  : <span className="flex items-center gap-1 text-xs text-gray-500"><AlertCircle className="w-3 h-3" />No email</span>;

// ─── ICAO Autocomplete (CORRIGÉ) ────────────────────────────────────────────

const IcaoInput = ({ value, onChange, className = '' }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const handleChange = (e) => {
    const raw = e.target.value.toUpperCase();
    onChange(raw);
    if (raw.length >= 2 && AIRPORTS_MAP) {
      const matches = Object.entries(AIRPORTS_MAP)
        .filter(([code, info]) => {
          if (!code || !info) return false;
          return code.startsWith(raw) ||
            (info.name && info.name.toUpperCase().includes(raw)) ||
            (info.city && info.city.toUpperCase().includes(raw));
        })
        .slice(0, 6)
        .map(([code, info]) => ({ code, ...info }));
      setSuggestions(matches);
      setOpen(matches.length > 0);
    } else {
      setOpen(false);
    }
  };

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const info = lookupIcao(value);

  return (
    <div ref={ref} className="relative">
      <input
        type="text"
        value={value || ''}
        onChange={handleChange}
        maxLength={4}
        placeholder="KDFW"
        className={`w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white uppercase tracking-widest placeholder:normal-case placeholder:tracking-normal ${className}`}
      />
      {info && info.name && (
        <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {info.name} — {info.city || ''}{info.state ? `, ${info.state}` : ''}
        </p>
      )}
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-xl">
          {suggestions.map(s => (
            <li 
              key={s.code} 
              onMouseDown={() => { onChange(s.code); setOpen(false); }}
              className="px-3 py-2 hover:bg-gray-700 cursor-pointer flex items-center gap-3"
            >
              <span className="text-white font-mono font-bold text-sm w-12">{s.code}</span>
              <span className="text-gray-300 text-sm truncate">{s.name || s.code}</span>
              <span className="text-gray-500 text-xs ml-auto">{s.city || ''}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ─── Composants de champ réutilisables ───────────────────────────────────────

const FieldInput = ({ label, value, onChange, type = 'text', placeholder = '', step = null }) => (
  <div>
    <label className="block text-xs text-gray-400 mb-1">{label}</label>
    <input
      type={type}
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      step={step}
      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
    />
  </div>
);

const FieldTextarea = ({ label, value, onChange, rows = 3 }) => (
  <div>
    <label className="block text-xs text-gray-400 mb-1">{label}</label>
    <textarea
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      rows={rows}
      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm resize-none"
    />
  </div>
);

// ─── Runway vide par défaut ───────────────────────────────────────────────────

const EMPTY_RUNWAY = { identifier: '', surface: '', length_ft: '', width_ft: '' };

// ─── Modal création / édition ────────────────────────────────────────────────

const EMPTY_FORM = {
  airport_code: '', airport_name: '', fbo_name: '', fuel_type: 'JetA',
  price_per_gallon: '', fuel_types: '', contact_email: '', contact_phone: '',
  manager_name: '', manager_email: '', manager_phone: '', website: '',
  city: '', state: '', zip_code: '', timezone: '', from_city: '',
  elevation_ft: '', latitude: '', longitude: '', airport_use: 'public', control_tower: false,
  attendance: '', ctaf_unicom: '', beacon: '', lights: '',
  parking: '', airframe_service: '', powerplant_service: '', services: '',
  runway_info: '', runway_surface: '', runway_length_ft: '', runway_width_ft: '',
  owner_name: '', owner_address: '', owner_phone: '', owner_email: '',
  notes: '', is_active: true,
};

const AirportModal = ({ airport, onClose, onSaved }) => {
  const isEdit = !!airport;
  
  // Pour l'édition, on fusionne l'airport API avec les données JSON
  const getInitialForm = () => {
    if (isEdit && airport) {
      const jsonData = lookupIcao(airport.airport_code);
      // Fusion : JSON d'abord, puis API écrase
      const merged = { ...EMPTY_FORM, ...jsonData, ...airport };
      // Nettoyer les champs undefined
      Object.keys(merged).forEach(k => {
        if (merged[k] === undefined) merged[k] = '';
      });
      return merged;
    }
    return { ...EMPTY_FORM };
  };
  
  const [form, setForm] = useState(getInitialForm);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('general');

  // ── Fuel prices ────────────────────────────────────────────────────────────
  const [fuelPrices, setFuelPrices] = useState([]);

  useEffect(() => {
    if (isEdit && airport) {
      if (airport.fuel_prices && airport.fuel_prices.length > 0) {
        setFuelPrices(airport.fuel_prices);
      } else if (airport.fuel_type && airport.price_per_gallon) {
        setFuelPrices([{ type: airport.fuel_type, price: parseFloat(airport.price_per_gallon) }]);
      } else {
        setFuelPrices([{ type: 'JetA', price: '' }]);
      }
    } else {
      setFuelPrices([{ type: 'JetA', price: '' }]);
    }
  }, [isEdit, airport]);

  // ── Runways ────────────────────────────────────────────────────────────────
  const [runways, setRunways] = useState([]);

  useEffect(() => {
    if (isEdit && airport) {
      // Priorité aux runways du JSON (plus complètes) ou de l'API
      const jsonData = lookupIcao(airport.airport_code);
      const sourceRunways = (airport.runways && airport.runways.length > 0) 
        ? airport.runways 
        : (jsonData?.runways || []);
      
      if (sourceRunways.length > 0) {
        setRunways(sourceRunways.map(r => ({
          identifier: r.identifier || '',
          surface:    r.surface    || '',
          length_ft:  r.length_ft  || '',
          width_ft:   r.width_ft   || '',
        })));
      } else if (airport.runway_length_ft || airport.runway_surface) {
        setRunways([{
          identifier: airport.runway_info || '',
          surface:    airport.runway_surface || '',
          length_ft:  airport.runway_length_ft || '',
          width_ft:   airport.runway_width_ft  || '',
        }]);
      } else {
        setRunways([{ ...EMPTY_RUNWAY }]);
      }
    } else {
      setRunways([{ ...EMPTY_RUNWAY }]);
    }
  }, [isEdit, airport]);

  const addRunway    = () => setRunways(r => [...r, { ...EMPTY_RUNWAY }]);
  const removeRunway = (i) => setRunways(r => r.filter((_, idx) => idx !== i));
  const updateRunway = (i, field, value) =>
    setRunways(r => r.map((rw, idx) => idx === i ? { ...rw, [field]: value } : rw));

  // ── Fuel helpers ───────────────────────────────────────────────────────────
  const addFuelPrice    = () => setFuelPrices(f => [...f, { type: 'JetA', price: '' }]);
  const removeFuelPrice = (i) => setFuelPrices(f => f.filter((_, idx) => idx !== i));
  const updateFuelPrice = (i, field, value) => {
    const updated = [...fuelPrices];
    updated[i][field] = field === 'price' ? parseFloat(value) : value;
    setFuelPrices(updated);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Auto-fill depuis airports.json quand ICAO saisie (mode création seulement)
  useEffect(() => {
    if (!isEdit && form.airport_code && form.airport_code.length === 4) {
      const info = lookupIcao(form.airport_code);
      if (info) {
        setForm(f => ({
          ...f,
          airport_name: f.airport_name || info.name || '',
          city:         f.city         || info.city || '',
          state:        f.state        || info.state || '',
          elevation_ft: f.elevation_ft || info.elevation_ft || '',
          latitude:     f.latitude     || info.lat || '',
          longitude:    f.longitude    || info.lon || '',
          runways:      f.runways?.length ? f.runways : (info.runways || []),
          fuel_types:   f.fuel_types   || info.fuel_types || '',
          timezone:     f.timezone     || info.timezone || '',
          website:      f.website      || info.website || '',
          contact_phone: f.contact_phone || info.contact_phone || '',
          manager_name: f.manager_name || info.manager_name || '',
          airport_use:  f.airport_use  || info.airport_use || 'public',
          ctaf_unicom:  f.ctaf_unicom  || info.ctaf_unicom || '',
        }));
      }
    }
  }, [form.airport_code, isEdit]);

  const handleSave = async () => {
    if (!form.airport_code) return;
    setSaving(true);
    try {
      const airportCode = form.airport_code;

      const cleanRunways = runways
        .filter(r => r.identifier || r.surface || r.length_ft || r.width_ft)
        .map(r => ({
          identifier: r.identifier || '',
          surface:    r.surface    || '',
          length_ft:  r.length_ft  ? parseInt(r.length_ft)  : null,
          width_ft:   r.width_ft   ? parseInt(r.width_ft)   : null,
        }));

      const firstRw = cleanRunways[0] || {};

      const existingFuelTypes = isEdit
        ? (airport?.fuel_prices?.length > 0
            ? airport.fuel_prices.map(f => f.type)
            : airport?.fuel_type ? [airport.fuel_type] : [])
        : [];

      if (fuelPrices && fuelPrices.length > 0) {
        for (const fuel of fuelPrices) {
          if (fuel.type && fuel.price && !isNaN(fuel.price)) {
            const payload = {
              airport_code:       airportCode,
              airport_name:       form.airport_name,
              fbo_name:           form.fbo_name,
              fuel_type:          fuel.type,
              price_per_gallon:   parseFloat(fuel.price),
              fuel_types:         form.fuel_types,
              contact_email:      form.contact_email,
              contact_phone:      form.contact_phone,
              manager_name:       form.manager_name,
              manager_email:      form.manager_email,
              manager_phone:      form.manager_phone,
              website:            form.website,
              city:               form.city,
              state:              form.state,
              zip_code:           form.zip_code,
              timezone:           form.timezone,
              from_city:          form.from_city,
              elevation_ft:       form.elevation_ft ? Math.round(parseFloat(form.elevation_ft)) : null,
              latitude:           form.latitude  ? parseFloat(form.latitude)  : null,
              longitude:          form.longitude ? parseFloat(form.longitude) : null,
              airport_use:        form.airport_use,
              control_tower:      form.control_tower,
              attendance:         form.attendance,
              ctaf_unicom:        form.ctaf_unicom,
              beacon:             form.beacon,
              lights:             form.lights,
              parking:            form.parking,
              airframe_service:   form.airframe_service,
              powerplant_service: form.powerplant_service,
              services:           form.services,
              runway_info:        firstRw.identifier || form.runway_info || null,
              runway_surface:     firstRw.surface    || form.runway_surface || null,
              runway_length_ft:   firstRw.length_ft  || null,
              runway_width_ft:    firstRw.width_ft   || null,
              runways:            cleanRunways,
              owner_name:         form.owner_name,
              owner_address:      form.owner_address,
              owner_phone:        form.owner_phone,
              owner_email:        form.owner_email,
              notes:              form.notes,
              is_active:          form.is_active,
            };

            Object.keys(payload).forEach(k => {
              if (payload[k] === '' || payload[k] === undefined) payload[k] = null;
            });
            if (payload.runways === null) payload.runways = [];

            if (isEdit && existingFuelTypes.includes(fuel.type)) {
              await axios.put(`${API_BASE}/admin/nearby-airports/${airportCode}`, payload);
            } else {
              await axios.post(`${API_BASE}/admin/nearby-airports/`, payload);
            }
          }
        }
      }

      onSaved();
    } catch (e) {
      console.error('Save error:', e);
      alert(e?.response?.data?.detail || 'Error saving airport');
    } finally {
      setSaving(false);
    }
  };

  const TABS = [
    { id: 'general',   label: 'General'    },
    { id: 'contact',   label: 'Contact'    },
    { id: 'ops',       label: 'Operations' },
    { id: 'runway',    label: 'Runways'    },
    { id: 'ownership', label: 'Ownership'  },
  ];

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-2xl border border-gray-700 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 flex-shrink-0">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Plane className="w-4 h-4 text-blue-400" />
            {isEdit ? `Edit — ${airport.airport_code}` : 'Add Airport'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-1 px-5 pt-3 border-b border-gray-700 flex-shrink-0 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-t-lg whitespace-nowrap transition ${tab === t.id ? 'bg-blue-500/20 text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'}`}>
              {t.label}
              {t.id === 'runway' && runways.filter(r => r.identifier || r.length_ft).length > 0 && (
                <span className="ml-1 bg-blue-500/30 text-blue-300 text-xs px-1.5 py-0.5 rounded-full">
                  {runways.filter(r => r.identifier || r.length_ft).length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto flex-1 p-5">
          {tab === 'general' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">ICAO Code *</label>
                {isEdit
                  ? <input value={form.airport_code} disabled className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-gray-400 text-sm cursor-not-allowed" />
                  : <IcaoInput value={form.airport_code} onChange={v => set('airport_code', v)} />
                }
              </div>
              <FieldInput label="Airport Name" value={form.airport_name} onChange={v => set('airport_name', v)} />
              <FieldInput label="FBO Name" value={form.fbo_name} onChange={v => set('fbo_name', v)} placeholder="e.g. Million Air, Signature" />

              <div className="border border-gray-700 rounded-lg p-3">
                <label className="block text-xs text-gray-400 mb-2">Fuel Prices</label>
                {fuelPrices.map((fuel, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <select
                      value={fuel.type}
                      onChange={e => updateFuelPrice(idx, 'type', e.target.value)}
                      className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                    >
                      <option value="JetA">Jet-A</option>
                      <option value="100LL">100LL</option>
                      <option value="SAF">SAF</option>
                    </select>
                    <input
                      type="number"
                      step="0.01"
                      value={fuel.price}
                      onChange={e => updateFuelPrice(idx, 'price', e.target.value)}
                      placeholder="$ / gal"
                      className="w-28 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeFuelPrice(idx)}
                      className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addFuelPrice}
                  className="mt-2 text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Add fuel type
                </button>
              </div>

              <FieldInput label="All Fuel Types (comma separated)" value={form.fuel_types} onChange={v => set('fuel_types', v)} placeholder="100LL, Jet-A, SAF" />
              <div className="grid grid-cols-3 gap-3">
                <FieldInput label="City" value={form.city} onChange={v => set('city', v)} />
                <FieldInput label="State" value={form.state} onChange={v => set('state', v)} placeholder="TX" />
                <FieldInput label="Zip" value={form.zip_code} onChange={v => set('zip_code', v)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FieldInput label="Elevation (ft)" value={form.elevation_ft} onChange={v => set('elevation_ft', v)} type="number" step="1" />
                <FieldInput label="Timezone" value={form.timezone} onChange={v => set('timezone', v)} placeholder="America/Chicago" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FieldInput label="Latitude" value={form.latitude} onChange={v => set('latitude', v)} type="number" step="any" placeholder="33.6367" />
                <FieldInput label="Longitude" value={form.longitude} onChange={v => set('longitude', v)} type="number" step="any" placeholder="-84.4281" />
              </div>
              <FieldInput label="From City" value={form.from_city} onChange={v => set('from_city', v)} placeholder="16 miles N of SAN ANTONIO, TX" />
              <FieldInput label="Website" value={form.website} onChange={v => set('website', v)} placeholder="https://" />
              <div className="flex items-center gap-2 mt-1">
                <input type="checkbox" id="is_active" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} className="rounded" />
                <label htmlFor="is_active" className="text-sm text-gray-300">Active</label>
              </div>
            </div>
          )}

          {tab === 'contact' && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500 bg-blue-500/10 border border-blue-500/20 rounded-lg px-3 py-2">
                📧 The contact email will be used to automatically notify this FBO when a client submits an away fuel request.
              </p>
              <FieldInput label="FBO Contact Email" value={form.contact_email} onChange={v => set('contact_email', v)} type="email" placeholder="fbo@airport.com" />
              <FieldInput label="FBO Contact Phone" value={form.contact_phone} onChange={v => set('contact_phone', v)} placeholder="+1 210-000-0000" />
              <div className="border-t border-gray-700 pt-3 mt-3">
                <p className="text-xs text-gray-400 mb-3 font-medium uppercase tracking-wider">Manager / Owner</p>
                <FieldInput label="Manager Name" value={form.manager_name} onChange={v => set('manager_name', v)} />
                <div className="mt-3" />
                <FieldInput label="Manager Email" value={form.manager_email} onChange={v => set('manager_email', v)} type="email" />
                <div className="mt-3" />
                <FieldInput label="Manager Phone" value={form.manager_phone} onChange={v => set('manager_phone', v)} />
              </div>
            </div>
          )}

          {tab === 'ops' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Airport Use</label>
                  <select value={form.airport_use ?? 'public'} onChange={e => set('airport_use', e.target.value)}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                    <option value="military">Military</option>
                  </select>
                </div>
                <div className="flex items-center gap-2 mt-5">
                  <input type="checkbox" id="ctrl" checked={form.control_tower} onChange={e => set('control_tower', e.target.checked)} />
                  <label htmlFor="ctrl" className="text-sm text-gray-300">Control Tower</label>
                </div>
              </div>
              <FieldInput label="Attendance Hours" value={form.attendance} onChange={v => set('attendance', v)} placeholder="0900-1700" />
              <FieldInput label="CTAF / UNICOM" value={form.ctaf_unicom} onChange={v => set('ctaf_unicom', v)} placeholder="122.8" />
              <FieldInput label="Beacon" value={form.beacon} onChange={v => set('beacon', v)} placeholder="white-green (lighted land airport)" />
              <FieldTextarea label="Lights" value={form.lights} onChange={v => set('lights', v)} rows={2} />
              <FieldInput label="Parking" value={form.parking} onChange={v => set('parking', v)} placeholder="hangars and tiedowns" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Airframe Service</label>
                  <select value={form.airframe_service ?? ''} onChange={e => set('airframe_service', e.target.value)}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="">—</option>
                    <option value="MAJOR">MAJOR</option>
                    <option value="MINOR">MINOR</option>
                    <option value="NONE">NONE</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Powerplant Service</label>
                  <select value={form.powerplant_service ?? ''} onChange={e => set('powerplant_service', e.target.value)}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="">—</option>
                    <option value="MAJOR">MAJOR</option>
                    <option value="MINOR">MINOR</option>
                    <option value="NONE">NONE</option>
                  </select>
                </div>
              </div>
              <FieldTextarea label="Additional Services" value={form.services} onChange={v => set('services', v)} rows={3} />
              <FieldTextarea label="Notes" value={form.notes} onChange={v => set('notes', v)} rows={3} />
            </div>
          )}

          {tab === 'runway' && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500 bg-gray-700/50 border border-gray-700 rounded-lg px-3 py-2">
                ✈️ Add all runways for this airport. Identifier format: <span className="font-mono text-gray-300">13/31</span> or <span className="font-mono text-gray-300">04L/22R</span>
              </p>

              {runways.map((rw, idx) => (
                <div key={idx} className="border border-gray-700 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Runway {idx + 1}
                    </span>
                    {runways.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRunway(idx)}
                        className="p-1 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Identifier</label>
                      <input
                        type="text"
                        value={rw.identifier}
                        onChange={e => updateRunway(idx, 'identifier', e.target.value)}
                        placeholder="13/31"
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Surface</label>
                      <select
                        value={rw.surface}
                        onChange={e => updateRunway(idx, 'surface', e.target.value)}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                      >
                        <option value="">— Select —</option>
                        <option value="Asphalt">Asphalt</option>
                        <option value="Concrete">Concrete</option>
                        <option value="Turf">Turf</option>
                        <option value="Gravel">Gravel</option>
                        <option value="Dirt">Dirt</option>
                        <option value="Water">Water</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Length (ft)</label>
                      <input
                        type="number"
                        value={rw.length_ft}
                        onChange={e => updateRunway(idx, 'length_ft', e.target.value)}
                        placeholder="8500"
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Width (ft)</label>
                      <input
                        type="number"
                        value={rw.width_ft}
                        onChange={e => updateRunway(idx, 'width_ft', e.target.value)}
                        placeholder="150"
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addRunway}
                className="w-full py-2.5 border border-dashed border-gray-600 hover:border-blue-500 rounded-lg text-gray-400 hover:text-blue-400 text-sm flex items-center justify-center gap-2 transition"
              >
                <Plus className="w-4 h-4" /> Add Runway
              </button>

              <FieldTextarea label="Additional Runway Notes (optional)" value={form.runway_info} onChange={v => set('runway_info', v)} rows={3} />
            </div>
          )}

          {tab === 'ownership' && (
            <div className="space-y-3">
              <FieldInput label="Owner Name" value={form.owner_name} onChange={v => set('owner_name', v)} />
              <FieldTextarea label="Owner Address" value={form.owner_address} onChange={v => set('owner_address', v)} rows={2} />
              <FieldInput label="Owner Phone" value={form.owner_phone} onChange={v => set('owner_phone', v)} />
              <FieldInput label="Owner Email" value={form.owner_email} onChange={v => set('owner_email', v)} type="email" />
            </div>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-700 flex-shrink-0">
          <button onClick={handleSave} disabled={saving || !form.airport_code}
            className="flex-1 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 rounded-lg text-white font-semibold text-sm transition">
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Airport'}
          </button>
          <button onClick={onClose}
            className="px-5 py-2.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm transition">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal upload cartes d'approche ─────────────────────────────────────────

const ChartsModal = ({ airport, onClose }) => {
  const [charts, setCharts] = useState(airport.approach_charts || []);
  const [uploading, setUploading] = useState(false);
  const [chartName, setChartName] = useState('');
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `${API_BASE}/admin/nearby-airports/${airport.airport_code}/chart?chart_name=${encodeURIComponent(chartName || file.name)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setCharts(res.data.charts);
      setChartName('');
    } catch (e) {
      alert('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm('Delete this chart?')) return;
    try {
      await axios.delete(`${API_BASE}/admin/nearby-airports/${airport.airport_code}/chart/${filename}`);
      setCharts(c => c.filter(x => x.filename !== filename));
    } catch {
      alert('Delete failed');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <FileText className="w-4 h-4 text-yellow-400" />
            Approach Charts — {airport.airport_code}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="space-y-2">
            <input value={chartName} onChange={e => setChartName(e.target.value)}
              placeholder="Chart name (optional)"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              className="w-full py-2.5 bg-blue-500/20 border border-blue-500/40 hover:bg-blue-500/30 rounded-lg text-blue-400 text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition">
              <Upload className="w-4 h-4" />
              {uploading ? 'Uploading…' : 'Upload PDF / Image'}
            </button>
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={handleUpload} className="hidden" />
          </div>

          {charts.length === 0
            ? <p className="text-center text-gray-500 text-sm py-4">No charts uploaded yet</p>
            : (
              <div className="space-y-2">
                {charts.map((c, i) => (
                  <div key={i} className="flex items-center gap-3 bg-gray-900 rounded-lg px-3 py-2.5">
                    <FileText className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{c.name}</p>
                      <p className="text-gray-500 text-xs">{new Date(c.uploaded_at).toLocaleDateString()}</p>
                    </div>
                    <a href={`${API_BASE}${c.url}`} target="_blank" rel="noreferrer"
                      className="text-blue-400 hover:text-blue-300 p-1">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <button onClick={() => handleDelete(c.filename)} className="text-red-400 hover:text-red-300 p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
};

// ─── Composant principal ─────────────────────────────────────────────────────

const NearbyAirportsManager = () => {
  const [airports, setAirports]    = useState([]);
  const [total, setTotal]          = useState(0);
  const [page, setPage]            = useState(1);
  const [pages, setPages]          = useState(1);
  const [loading, setLoading]      = useState(true);
  const [search, setSearch]        = useState('');
  const [stateFilter, setStateFi]  = useState('');
  const [fuelFilter, setFuelFi]    = useState('');
  const [showModal, setShowModal]  = useState(false);
  const [editAirport, setEdit]     = useState(null);
  const [chartsAirport, setCharts] = useState(null);
  const LIMIT = 20;

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: LIMIT };
      if (search) params.search = search;
      if (stateFilter) params.state = stateFilter;
      if (fuelFilter) params.fuel_type = fuelFilter;

      const res = await axios.get(`${API_BASE}/admin/nearby-airports/`, { params });

      // Regrouper par airport_code et fusionner avec les données JSON
      const airportsMap = new Map();
      (res.data.airports || []).forEach(ap => {
        const code = ap.airport_code;
        
        // FUSION avec les données statiques du JSON
        const mergedAp = mergeWithJsonData(ap);
        
        if (!airportsMap.has(code)) {
          airportsMap.set(code, { ...mergedAp, fuel_prices: [] });
        }

        const existing = airportsMap.get(code);

        if (ap.fuel_type && ap.price_per_gallon) {
          const exists = existing.fuel_prices.some(f => f.type === ap.fuel_type);
          if (!exists) {
            existing.fuel_prices.push({
              type:  ap.fuel_type,
              price: parseFloat(ap.price_per_gallon)
            });
          }
        }

        Object.keys(ap).forEach(key => {
          if (ap[key] !== null && ap[key] !== undefined && key !== 'fuel_type' && key !== 'price_per_gallon') {
            if (!existing[key] || (ap.last_updated && ap.last_updated > existing.last_updated)) {
              existing[key] = ap[key];
            }
          }
        });
      });

      setAirports(Array.from(airportsMap.values()));
      setTotal(res.data.total);
      setPages(res.data.pages);
      setPage(p);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, stateFilter, fuelFilter]);

  useEffect(() => { load(1); }, [load]);

  const handleDelete = async (icao) => {
    if (!window.confirm(`Delete ${icao}?`)) return;
    try {
      await axios.delete(`${API_BASE}/admin/nearby-airports/${icao}`);
      load(page);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error');
    }
  };

  const onSaved = () => {
    setShowModal(false);
    setEdit(null);
    load(page);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Globe className="w-5 h-5 text-cyan-400" />
            Nearby Airports
          </h2>
          <p className="text-gray-400 text-xs mt-0.5">{total} airports in database</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => load(page)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => { setEdit(null); setShowModal(true); }}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white text-sm flex items-center gap-2 transition">
            <Plus className="w-4 h-4" /> Add Airport
          </button>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-500" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search ICAO, name, city, FBO…"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-3 py-2 text-white text-sm" />
        </div>
        <input value={stateFilter} onChange={e => setStateFi(e.target.value.toUpperCase())}
          placeholder="State (TX)"
          maxLength={2}
          className="w-24 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm uppercase" />
        <select value={fuelFilter} onChange={e => setFuelFi(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm">
          <option value="">All fuels</option>
          <option value="JetA">Jet-A</option>
          <option value="100LL">100LL</option>
          <option value="SAF">SAF</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>
      ) : airports.length === 0 ? (
        <div className="text-center py-12 bg-gray-800/50 rounded-xl border border-gray-700">
          <Globe className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500">No airports found</p>
          <button onClick={() => { setEdit(null); setShowModal(true); }} className="mt-3 text-blue-400 text-sm hover:text-blue-300">
            Add the first airport
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {airports.map(ap => {
            const chartsCount = (ap.approach_charts || []).length;
            const rwList = ap.runways && ap.runways.length > 0
              ? ap.runways
              : (ap.runway_length_ft ? [{ 
                  identifier: ap.runway_info || '', 
                  length_ft: ap.runway_length_ft, 
                  width_ft: ap.runway_width_ft, 
                  surface: ap.runway_surface 
                }] : []);

            return (
              <div key={ap.id || ap.airport_code}
                className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 hover:border-gray-600 transition">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-16 h-16 bg-blue-500/10 border border-blue-500/20 rounded-xl flex flex-col items-center justify-center">
                    <span className="text-blue-400 font-mono font-bold text-lg leading-none">{ap.airport_code}</span>
                    {ap.is_active
                      ? <span className="text-xs text-green-400 mt-1">Active</span>
                      : <span className="text-xs text-gray-500 mt-1">Inactive</span>}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-white font-semibold">
                          {ap.fbo_name || ap.airport_name || ap.airport_code}
                        </p>
                        {ap.airport_name && ap.fbo_name && (
                          <p className="text-gray-400 text-xs">{ap.airport_name}</p>
                        )}
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button onClick={() => setCharts(ap)}
                          className="p-1.5 bg-yellow-500/20 hover:bg-yellow-500/30 rounded-lg text-yellow-400 transition" title="Approach Charts">
                          <FileText className="w-4 h-4" />
                          {chartsCount > 0 && <span className="text-xs ml-0.5">{chartsCount}</span>}
                        </button>
                        <button onClick={() => { setEdit(ap); setShowModal(true); }}
                          className="p-1.5 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400 transition" title="Edit">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(ap.airport_code)}
                          className="p-1.5 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 mt-2">
                      {(ap.city) && (
                        <span className="flex items-center gap-1 text-xs text-gray-400">
                          <MapPin className="w-3 h-3" />
                          {ap.city}{ap.state ? `, ${ap.state}` : ''}
                          {(ap.elevation_ft) ? ` — ${ap.elevation_ft} ft` : ''}
                        </span>
                      )}
                      {ap.fuel_types && (
                        <span className="flex items-center gap-1 text-xs text-orange-400">
                          <Fuel className="w-3 h-3" />{ap.fuel_types}
                        </span>
                      )}
                      <div className="flex flex-wrap gap-1.5">
                        {ap.fuel_prices && ap.fuel_prices.length > 0 ? (
                          ap.fuel_prices.map((fuel, idx) => (
                            <span key={idx} className="text-xs font-semibold text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full">
                              {fuel.type}: ${typeof fuel.price === 'number' ? fuel.price.toFixed(2) : parseFloat(fuel.price).toFixed(2)}/gal
                            </span>
                          ))
                        ) : ap.price_per_gallon ? (
                          <span className="text-xs font-semibold text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full">
                            ${parseFloat(ap.price_per_gallon).toFixed(2)}/gal
                          </span>
                        ) : null}
                      </div>
                      {ap.ctaf_unicom && (
                        <span className="flex items-center gap-1 text-xs text-cyan-400">
                          <Radio className="w-3 h-3" />{ap.ctaf_unicom}
                        </span>
                      )}
                      {rwList.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {rwList.map((rw, idx) => (
                            <span key={idx} className="flex items-center gap-1 text-xs text-gray-400 bg-gray-700/50 px-2 py-0.5 rounded-full">
                              <Wind className="w-3 h-3" />
                              {rw.identifier && <span className="font-mono">{rw.identifier}</span>}
                              {rw.length_ft && <span>{rw.length_ft}{rw.width_ft ? `×${rw.width_ft}` : ''} ft</span>}
                              {rw.surface && <span className="text-gray-500">{rw.surface}</span>}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-3 mt-1.5">
                      <EmailBadge email={ap.contact_email || ap.manager_email} />
                      {ap.contact_phone && (
                        <span className="flex items-center gap-1 text-xs text-gray-400">
                          <Phone className="w-3 h-3" />{ap.contact_phone}
                        </span>
                      )}
                      {ap.latitude && ap.longitude && (
                        <a href={`https://maps.google.com/?q=${ap.latitude},${ap.longitude}`} target="_blank" rel="noreferrer"
                          className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition">
                          <MapPin className="w-3 h-3" />{parseFloat(ap.latitude).toFixed(4)}, {parseFloat(ap.longitude).toFixed(4)}
                        </a>
                      )}
                      {ap.last_verified_at && (
                        <span className="text-xs text-gray-500">Verified {formatDate(ap.last_verified_at)}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <button onClick={() => load(page - 1)} disabled={page === 1}
            className="p-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-30 rounded-lg text-white transition">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-gray-400 text-sm">Page {page} / {pages}</span>
          <button onClick={() => load(page + 1)} disabled={page === pages}
            className="p-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-30 rounded-lg text-white transition">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {showModal && (
        <AirportModal
          airport={editAirport}
          onClose={() => { setShowModal(false); setEdit(null); }}
          onSaved={onSaved}
        />
      )}
      {chartsAirport && (
        <ChartsModal airport={chartsAirport} onClose={() => setCharts(null)} />
      )}
    </div>
  );
};

export default NearbyAirportsManager;